/**
 * SECURITY: Generic error handling for authentication
 * Replaces Firebase-specific error messages with generic ones
 * Prevents attackers from enumerating valid email addresses
 * Prevents information disclosure about auth state
 */

/**
 * Firebase error codes that should be hidden from users
 * SECURITY: These reveal information about whether email exists or password is correct
 */
const ENUMERABLE_ERROR_CODES = Object.freeze({
  'auth/user-not-found': true,
  'auth/wrong-password': true,
  'auth/user-disabled': true,
  'auth/invalid-email': true,
});

/**
 * SECURITY: Maps Firebase errors to generic messages
 * Prevents information leakage while still providing helpful feedback
 *
 * @param {Error} error - Firebase auth error
 * @returns {object} { message: string, isRetryable: boolean, isRateLimit: boolean }
 */
export const getGenericAuthErrorMessage = (error) => {
  // SECURITY: Always log actual error server-side for debugging
  console.error('[AUTH_ERROR_DEBUG]', error?.code, error?.message);

  if (!error || !error.code) {
    return {
      message: 'An error occurred. Please try again.',
      isRetryable: true,
      isRateLimit: false,
    };
  }

  // SECURITY: Handle rate limiting explicitly (user should not retry immediately)
  if (error.code === 'auth/too-many-requests') {
    return {
      message:
        'Too many login attempts. Please try again later or reset your password.',
      isRetryable: false,
      isRateLimit: true,
    };
  }

  // SECURITY: Handle network errors (user should retry)
  if (
    error.code === 'auth/network-request-failed' ||
    error.message?.includes('network')
  ) {
    return {
      message: 'Network error. Please check your connection and try again.',
      isRetryable: true,
      isRateLimit: false,
    };
  }

  // SECURITY: Hide all enumerable errors behind generic message
  if (ENUMERABLE_ERROR_CODES[error.code]) {
    return {
      message: 'Invalid email or password. Please try again.',
      isRetryable: true,
      isRateLimit: false,
    };
  }

  // SECURITY: Handle operation-not-allowed (auth not configured)
  if (error.code === 'auth/operation-not-allowed') {
    return {
      message:
        'This authentication method is not available. Please contact support.',
      isRetryable: false,
      isRateLimit: false,
    };
  }

  // SECURITY: Handle invalid credentials
  if (error.code === 'auth/invalid-credential') {
    return {
      message: 'Invalid credentials. Please try again.',
      isRetryable: true,
      isRateLimit: false,
    };
  }

  // SECURITY: Default fallback
  return {
    message: 'Authentication failed. Please try again or contact support.',
    isRetryable: true,
    isRateLimit: false,
  };
};

/**
 * SECURITY: Generic error message for password reset
 * Always returns same message whether email exists or not
 * Prevents email enumeration attacks
 *
 * @returns {string} Generic password reset message
 */
export const getPasswordResetMessage = () => {
  return (
    'If an account exists with that email address, ' +
    'you will receive a password reset link. Please check your inbox and spam folder.'
  );
};

/**
 * SECURITY: Maps Firebase MFA errors to generic messages
 *
 * @param {Error} error - Firebase MFA error
 * @returns {object} { message: string, isRetryable: boolean }
 */
export const getGenericMFAErrorMessage = (error) => {
  console.error('[MFA_ERROR_DEBUG]', error?.code, error?.message);

  if (!error || !error.code) {
    return {
      message: 'An error occurred. Please try again.',
      isRetryable: true,
    };
  }

  // SECURITY: Handle invalid verification code
  if (error.code === 'auth/invalid-verification-code') {
    return {
      message: 'Invalid code. Please check the code and try again.',
      isRetryable: true,
    };
  }

  // SECURITY: Handle code expired
  if (error.code === 'auth/code-expired') {
    return {
      message: 'Code expired. Please request a new code.',
      isRetryable: false,
    };
  }

  // SECURITY: Handle rate limiting
  if (error.code === 'auth/too-many-requests') {
    return {
      message: 'Too many attempts. Please try again later.',
      isRetryable: false,
    };
  }

  return {
    message: 'MFA verification failed. Please try again.',
    isRetryable: true,
  };
};

/**
 * SECURITY: Determines if user should be allowed to retry an operation
 * Used to implement client-side rate limiting feedback
 *
 * @param {boolean} isRateLimit
 * @returns {boolean}
 */
export const shouldAllowRetry = (isRateLimit) => {
  return !isRateLimit;
};
